/**
 * The employers who came to us, and how they join the outreach list.
 *
 * Three tables record the same event — somebody raised their hand.
 * `company_leads` is a demo request from the registration form,
 * `contact_messages` is the public contact form, and both were reachable only
 * as raw tables with no next action, no follow-up date, no owner and no history:
 * exactly the machinery that exists for a cold scraped employer and did not
 * exist for a warm one. Against the production copy every inbound row was still
 * sitting at its first status, which is what that costs.
 *
 * So they stop being screens and become a *source*. An inbound row is taken into
 * `company_prospects` with `source = 'inbound'`, where it sorts ahead of every
 * cold employer, and the row it came from records which prospect it became. The
 * inbound tables keep every column they had; nothing is migrated and nothing is
 * deleted. `employer_key` on the source row is the whole of the change, and a
 * NULL there is the definition of "nobody has worked this yet".
 *
 * ── Why the source row is not the record ─────────────────────────────
 *
 * One employer can ask twice — a demo request in March, a contact-form message
 * in June — and the outreach record has to be one account with one history, not
 * two rows that each know half of it. Taking the second one finds the existing
 * prospect by key and attaches to it rather than creating a rival.
 */

import { employerFromAddress, employerKey } from "../employer_identity";
import { contactNameKey, type ProspectRow } from "./_prospects_shared";

export type InboundKind = "lead" | "message";

export const INBOUND_LABEL: Record<InboundKind, string> = {
    // Named by where they came from, not by what we call them. A "lead" is
    // a company that registered itself on sustentalent.com; a "message" is
    // the contact form on the same site. Neither is an email.
    lead: "Se registró en la web",
    message: "Formulario de contacto de la web"
};

export const INBOUND_COLOR: Record<InboundKind, string> = {
    lead: "greenLighter",
    message: "cyanLighter"
};

/** One raised hand, whichever table it arrived in. */
export interface InboundItem {
    id: string;
    kind: InboundKind;
    /** The employer, as best we can name them. Empty when we cannot. */
    employerName: string;
    /** Who wrote, when that is a person distinct from the company. */
    personName: string | null;
    /** Their role, when the signup form asked for one. */
    personPosition?: string | null;
    personPhone?: string | null;
    email: string | null;
    /** What they said, in their words. */
    body: string;
    createdAt: string | null;
    /** Set once somebody has taken this into the list. NULL is the queue. */
    employerKey: string | null;
    /** Leads already carry a companies row; the prospect inherits the link. */
    companyId: string | null;
    website: string | null;
    /**
     * The key this would be taken into, or null when it cannot be worked out.
     *
     * A contact-form message from a personal address gives no employer at all,
     * and inventing one from "gmail.com" would put Gmail on the prospect list.
     * The screen shows why rather than offering a button that makes a mess.
     */
    resolvedKey: string | null;
}

interface LeadRow {
    id: string;
    company_id?: string | null;
    needs_description?: string | null;
    requested_services?: string[] | null;
    employer_key?: string | null;
    created_at?: string | null;
}

interface MemberRow {
    id: string;
    company_id?: string | null;
    contact_name?: string | null;
    contact_position?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    created_at?: string | null;
}

/** An open request to manage the company (drizzle/0062): the person, when no member exists yet. */
interface ClaimRow {
    company_id?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
    email?: string | null;
    status?: string | null;
    created_at?: string | null;
}

interface MessageRow {
    id: string;
    name?: string | null;
    email?: string | null;
    subject?: string | null;
    message?: string | null;
    status?: string | null;
    employer_key?: string | null;
    created_at?: string | null;
}

interface CompanyRow {
    id: string;
    name?: string | null;
    website?: string | null;
    domain?: string | null;
    employer_key?: string | null;
}

const SERVICE_LABEL: Record<string, string> = {
    headhunting: "selección y contratación",
    attraction_optimization: "atracción y optimización"
};

/**
 * Everything inbound, taken and untaken, newest first.
 *
 * Both tables are read whole: five leads and one message today, and a business
 * that has thousands of either has a different problem than this screen. Reading
 * them whole is what lets "taken" and "untaken" both be counted without a second
 * round trip per tab.
 */
export async function fetchInbound(data: any): Promise<InboundItem[]> {
    const [leadPage, messagePage] = await Promise.all([
        data.collection("company-leads").find({ limit: 500, orderBy: ["created_at", "desc"] })
            .catch(() => undefined),
        data.collection("contact-messages").find({ limit: 500, orderBy: ["created_at", "desc"] })
            .catch(() => undefined)
    ]);

    const leads = (leadPage?.data ?? []) as LeadRow[];
    const messages = (messagePage?.data ?? []) as MessageRow[];

    // A lead names its company by id and nothing else, so the names come from a
    // second read — one request for all of them, not one per row.
    const companyIds = [...new Set(leads.map(l => l.company_id).filter((id): id is string => !!id))];
    const companies = new Map<string, CompanyRow>();
    /**
     * The person who registered the company, from `company_members`.
     *
     * A lead is written by the signup form and carries no contact of its own:
     * the person is the member row the same signup created. Without this the
     * screen said "AmSpec pidió: selección y contratación" and nothing about
     * who, so the one action offered was to file it — not to answer it. First
     * member per company, which for a self-registered company is the
     * registrant.
     */
    const members = new Map<string, MemberRow>();
    /**
     * A lead written by `claim-company` for somebody asking to manage a company
     * we already held. Nobody is a member yet — a person has to approve them —
     * so the person comes from the request itself, and the item says what they
     * asked for rather than "se registró".
     */
    const claims = new Map<string, ClaimRow>();
    if (companyIds.length > 0) {
        const [page, memberPage, claimPage] = await Promise.all([
            data.collection("companies")
                .find({ where: { id: ["in", companyIds] }, limit: companyIds.length })
                .catch(() => undefined),
            data.collection("company-members")
                .find({ where: { company_id: ["in", companyIds] }, orderBy: ["created_at", "asc"], limit: 500 })
                .catch(() => undefined),
            data.collection("company-claims")
                .find({ where: { company_id: ["in", companyIds], status: ["==", "pending"] }, orderBy: ["created_at", "desc"], limit: 500 })
                .catch(() => undefined)
        ]);
        for (const row of ((page?.data ?? []) as CompanyRow[])) {
            if (row?.id) companies.set(row.id, row);
        }
        for (const row of ((memberPage?.data ?? []) as MemberRow[])) {
            if (row?.company_id && !members.has(row.company_id)) members.set(row.company_id, row);
        }
        for (const row of ((claimPage?.data ?? []) as ClaimRow[])) {
            if (row?.company_id && !claims.has(row.company_id)) claims.set(row.company_id, row);
        }
    }

    const items: InboundItem[] = [];

    for (const lead of leads) {
        const company = lead.company_id ? companies.get(lead.company_id) : undefined;
        const member = lead.company_id ? members.get(lead.company_id) : undefined;
        const claim = !member && lead.company_id ? claims.get(lead.company_id) : undefined;
        const name = (company?.name ?? "").trim();
        const services = (lead.requested_services ?? [])
            .map(s => SERVICE_LABEL[s] ?? s)
            .join(", ");
        items.push({
            id: lead.id,
            kind: "lead",
            employerName: name,
            personName: (member?.contact_name ?? claim?.contact_name ?? "").trim() || null,
            personPosition: (member?.contact_position ?? "").trim() || null,
            personPhone: (member?.contact_phone ?? claim?.contact_phone ?? "").trim() || null,
            // A claimant's address is the one on their account, read by the
            // server when they asked — the same provenance a member's has.
            email: (member?.contact_email ?? claim?.email ?? "").trim() || null,
            body: [
                claim ? "Pide gestionar la ficha de la empresa: apruébalo o recházalo en su ficha." : "",
                services ? `Marcó: ${services}.` : "",
                (lead.needs_description ?? "").trim()
            ].filter(Boolean).join(" ") || "Se registró como empresa en sustentalent.com.",
            createdAt: lead.created_at ?? null,
            employerKey: lead.employer_key ?? null,
            companyId: lead.company_id ?? null,
            website: company?.website ?? (company?.domain ? `https://${company.domain}` : null),
            // A lead always resolves: it is attached to a real companies row, so
            // the name exists even when nobody has typed a website.
            resolvedKey: company?.employer_key || (name ? employerKey(name) : null)
        });
    }

    for (const message of messages) {
        const derived = employerFromAddress(message.email);
        items.push({
            id: message.id,
            kind: "message",
            employerName: derived?.name ?? "",
            personName: (message.name ?? "").trim() || null,
            email: (message.email ?? "").trim() || null,
            body: [(message.subject ?? "").trim(), (message.message ?? "").trim()]
                .filter(Boolean).join(" — ") || "(sin texto)",
            createdAt: message.created_at ?? null,
            employerKey: message.employer_key ?? null,
            companyId: null,
            website: derived?.website ?? null,
            resolvedKey: derived?.key ?? null
        });
    }

    return items.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

/** Untaken, and therefore work. Taken rows stay readable but leave the queue. */
export function isUntaken(item: InboundItem): boolean {
    return !item.employerKey;
}

export function inboundSourceDetail(item: InboundItem): string {
    return item.kind === "lead"
        ? "Se registró como empresa en sustentalent.com"
        : `Escribió por el formulario de contacto de sustentalent.com${item.personName ? `: ${item.personName}` : ""}`;
}

/**
 * Take an inbound row into the outreach list.
 *
 * Finds the prospect by key before creating one, because the same employer can
 * arrive twice and the second arrival must join the first account rather than
 * open a rival to it. Returns the key that was used, so the caller can select
 * the employer the operator just created.
 *
 * The order matters and is deliberate: the prospect first, the contact second,
 * the stamp on the source row last. A failure part-way leaves an employer on the
 * list with the inbound row still in the queue — which is visible and fixable by
 * pressing the button again — rather than a source row marked as handled with no
 * prospect behind it, which nothing would ever surface.
 */
export async function takeInbound(
    data: any,
    item: InboundItem,
    existing: ProspectRow | undefined
): Promise<string> {
    const key = item.resolvedKey;
    if (!key) throw new Error("No se puede deducir la empresa de este mensaje.");

    if (!existing) {
        try {
            await data.collection("company-prospects").create({
                employer_key: key,
                employer_name: item.employerName || key,
                employer_website: item.website,
                company_id: item.companyId,
                source: "inbound",
                source_detail: inboundSourceDetail(item)
            });
        } catch (err: any) {
            // Somebody else took the same employer between the read and the
            // click. The row exists, which is what the rest of this needs.
            if (!/unique|duplicate|already exists/i.test(String(err?.message ?? ""))) throw err;
        }
    } else if (!existing.company_id && item.companyId) {
        // The employer was already on the list from a scrape, and this inbound
        // row knows the companies row that the scrape never resolved. Worth
        // taking; it is the reconciliation 0044 exists for, arriving by hand.
        await data.collection("company-prospects")
            .update(existing.id, { company_id: item.companyId })
            .catch(() => { /* the link is a bonus, not the point of this click */ });
    }

    // The person who wrote is a contact, and this is the one moment their name
    // and address are both in hand. Only for messages: a lead names a company
    // and no human, and inventing one from the company name would be a contact
    // card for somebody who does not exist.
    if (item.kind === "message" && item.email) {
        // The canonical derivation, not a copy of it: the backend's discovery
        // run folds the same person through `contactNameKey` and the UNIQUE
        // index expects one answer. Two implementations would put the same human
        // on the card twice.
        const nameKey = contactNameKey({ full_name: item.personName, email: item.email });
        await data.collection("prospect-contacts").create({
            employer_key: key,
            name_key: nameKey,
            kind: item.personName ? "person" : "mailbox",
            full_name: item.personName,
            email: item.email,
            // They wrote it themselves, from it. Nothing published is more
            // reliable than that, and discovery must never overwrite it.
            email_status: "published",
            email_alternatives: [],
            source: "manual",
            notes: "Escribió por el formulario de contacto."
        }).catch((err: any) => {
            // Already known — the same person writing twice is not an error.
            if (!/unique|duplicate|already exists/i.test(String(err?.message ?? ""))) throw err;
        });
    }

    await data.collection("prospect-activity").create({
        employer_key: key,
        kind: "note",
        body: `Entró por ${item.kind === "lead" ? "un lead comercial" : "el formulario de contacto"}: ${item.body}`.slice(0, 2000),
        actor: null
    }).catch(() => { /* the log is not worth failing the take */ });

    await data.collection(item.kind === "lead" ? "company-leads" : "contact-messages")
        .update(item.id, { employer_key: key });

    return key;
}
